import { ATHLETE_MANAGER_PAGE_SIZE_DEFAULT, ATHLETE_MANAGER_ROLES, type RoleCode } from "@seagames/contracts";
import { Pagination, useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { type AthleteFix, AthleteCard } from "../../components/athlete-manager/athlete-card";
import { AthleteManagerFilters } from "../../components/athlete-manager/athlete-manager-filters";
import { athleteManagerKeys, athleteManagerListQuery, enterAthlete, shortlistAthlete } from "../../components/athlete-manager/queries";
import {
	type AthleteManagerDraft,
	type AthleteManagerSearch,
	EMPTY_ATHLETE_MANAGER_DRAFT,
	athleteManagerParams,
	athleteManagerSearch,
	draftFrom,
	filterCount,
	isReady,
	paging,
	searchFrom,
} from "../../components/athlete-manager/search";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { nocOptionsQuery } from "../../components/nocs/queries";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { toastApiError, toastSuccess } from "../../components/ui/toast";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { nocName } from "../../components/nocs/noc-name";

/**
 * Tools › Athlete manager (the old OVR's tool): why an athlete is not on an
 * OVR start list — each athlete card of the current games with its
 * shortlist and entries, and where the chain card → shortlist → entry stops
 * (`GET /athlete-manager`). Filters in the URL; nothing is fetched until a
 * name, an NOC or a sport is set (the API needs one). The fixes are the
 * existing writes (an entry, a shortlist row) or a link to the card in GMS;
 * each confirms first and refetches the page after.
 */
export const Route = createFileRoute("/_app/athlete-manager")({
	validateSearch: athleteManagerSearch,
	component: AthleteManagerPage,
});

const MAY_USE = (role: RoleCode | null) => role !== null && (ATHLETE_MANAGER_ROLES as readonly RoleCode[]).includes(role);

function AthleteManagerPage() {
	const { t } = useTranslation("athleteManager");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/athlete-manager" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;
	const allowed = MAY_USE(session.active_role);

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null && allowed });
	const nocs = useQuery({ ...nocOptionsQuery(gamesId ?? ""), enabled: gamesId !== null && allowed });

	const go = useCallback((next: AthleteManagerSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	const applied = useMemo(() => draftFrom(search), [search]);
	const onApply = useCallback((d: AthleteManagerDraft) => go(searchFrom(d, search.size)), [go, search.size]);
	const f = useFilterDraft<AthleteManagerDraft>(applied, onApply, EMPTY_ATHLETE_MANAGER_DRAFT);
	const count = filterCount(search);

	// The sidebar switched games: NOCs keep their ids, sports do not.
	useOnGamesSwitch(gamesId, () => go({ ...search, sport: undefined, page: undefined }, true));

	const ready = isReady(search);
	const params = gamesId !== null && ready && allowed ? athleteManagerParams(gamesId, search) : null;
	const list = useQuery({
		...athleteManagerListQuery(params ?? { games_id: "", page: 1, page_size: ATHLETE_MANAGER_PAGE_SIZE_DEFAULT }),
		enabled: params !== null,
		placeholderData: keepPreviousData,
	});

	const { page, pageSize } = paging(search);
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	// ── Fixes ────────────────────────────────────────────────────────────────
	const queryClient = useQueryClient();
	const [pending, setPending] = useState<AthleteFix | null>(null);
	const fix = useMutation({
		mutationFn: async (x: AthleteFix) => {
			if (x.kind === "enter") {
				const org = x.athlete.organization?.responsible_organization_id;
				if (!org) throw new Error("no organisation");
				await enterAthlete(x.item.event.event_id, org, x.athlete.people_id);
				return { x, refused: null };
			}
			const result = await shortlistAthlete(x.athlete.ac_card_id, x.item.event.event_id);
			return { x, refused: result.refused[0] ?? null, unchanged: result.unchanged > 0 };
		},
		onSuccess: ({ x, refused, unchanged }) => {
			const vars = { name: x.athlete.name, event: x.item.event.name };
			if (refused) {
				toast.error(`${t("refused.title")}: ${t(`refused.${refused.reason}`, { used: refused.used ?? 0, limit: refused.limit ?? 0 })}`);
			} else if (x.kind === "enter") toastSuccess(t("done.entered", vars));
			else toastSuccess(unchanged ? t("done.unchanged", vars) : t("done.shortlisted", vars));
			setPending(null);
		},
		onError: (e) => {
			toastApiError(e);
			setPending(null);
		},
		onSettled: () => void queryClient.invalidateQueries({ queryKey: athleteManagerKeys.all }),
	});

	if (!allowed) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.forbiddenTitle")} body={t("empty.forbiddenBody")} />
			</div>
		);
	}
	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;
	const rows = list.data?.items ?? [];
	const loading = list.isPending || (list.isFetching && list.isPlaceholderData);

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />
			<p className="mb-[1rem] max-w-[70ch] text-[0.875rem] text-muted">{t("chain")}</p>

			<div className="flex flex-col gap-[1rem]">
				<AthleteManagerFilters
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={count}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
				/>

				<div ref={top} className="flex scroll-mt-[4.5rem] flex-col gap-[0.75rem]">
					{!ready ? (
						<EmptyState title={t("empty.promptTitle")} body={t("empty.promptBody")} />
					) : list.isError ? (
						<EmptyState
							title={t("empty.errorTitle")}
							body={errorMessage(list.error)}
							action={
								<Button variant="outline" size="sm" onClick={() => void list.refetch()}>
									{t("actions.retry", { ns: "common" })}
								</Button>
							}
						/>
					) : loading ? (
						<Spinner className="size-[1.5rem] text-board" />
					) : rows.length === 0 ? (
						<EmptyState
							title={t("empty.noneTitle")}
							body={t("empty.noneBody")}
							action={
								<Button variant="outline" size="sm" onClick={f.reset}>
									{t("empty.reset")}
								</Button>
							}
						/>
					) : (
						<>
							<span className="text-[0.8125rem] text-muted" aria-live="polite">
								{t("table.count", { count: total, n: formatInteger(total) })}
							</span>
							{rows.map((a) => (
								<AthleteCard key={a.ac_card_id} athlete={a} busy={fix.isPending} onFix={setPending} />
							))}
							<Pagination
								page={page}
								pageSize={pageSize}
								total={total}
								onPageChange={(p) => go({ ...search, page: pageToUrl(p) })}
								onPageSizeChange={(s) => go({ ...search, size: sizeToUrl(s, ATHLETE_MANAGER_PAGE_SIZE_DEFAULT), page: undefined })}
								pageSizeOptions={PAGE_SIZE_CHOICES}
							/>
						</>
					)}
				</div>
			</div>

			<ConfirmDialog
				open={pending !== null}
				title={
					pending
						? t(pending.kind === "enter" ? "confirm.enterTitle" : "confirm.shortlistTitle", {
								name: pending.athlete.name,
								event: pending.item.event.name,
							})
						: ""
				}
				body={
					pending
						? t(pending.kind === "enter" ? "confirm.enterBody" : "confirm.shortlistBody", {
								name: pending.athlete.name,
								noc: pending.athlete.organization ? (pending.athlete.organization.code ?? nocName(pending.athlete.organization)) : "",
							})
						: undefined
				}
				confirmLabel={pending?.kind === "enter" ? t("actions.enter") : t("actions.shortlist")}
				pending={fix.isPending}
				onConfirm={() => pending && fix.mutate(pending)}
				onCancel={() => setPending(null)}
			/>
		</div>
	);
}
